import React, { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { format } from 'date-fns';
import { Card, CardHeader, CardContent } from '@/components/ui/Card';
import Button from '@/components/ui/Button';
import { Spinner } from '@/components/ui/Spinner';
import {
  DropdownMenu,
  DropdownMenuTrigger,
  DropdownMenuContent,
  DropdownMenuItem,
} from '@/components/ui/DropdownMenu';
import AppIcon from '@/components/AppIcon';
import { cn } from '@/utils/cn';
import defaultProfile from '../../../../assets/images/defaultProfile.png';
import { getFullUrl } from '../../../../utils/url';

interface Update {
  id: number;
  content: string;
  type: string;
  milestone?: string;
  attachments: Array<{
    id: number;
    name: string;
    size: number;
    type: string;
  }>;
  timestamp: Date;
  author: {
    name: string;
    role: string;
    avatar?: string;
    avatarAlt: string;
  };
  department: string;
}

interface UpdateCardProps {
  update: Update;
  isAuthor: boolean;
  comments: any[];
  onComment: (comment: string) => Promise<void>;
  onDelete: () => void;
  onDeleteComment: (commentId: number) => void;
  canDeleteComment: (commentAuthorId: string) => boolean;
}

const UpdateCard: React.FC<UpdateCardProps> = ({
  update,
  isAuthor,
  comments: initialComments = [],
  onComment,
  onDelete,
  onDeleteComment,
  canDeleteComment,
}) => {
  const { t } = useTranslation();
  const [showComments, setShowComments] = useState(false);
  const [commentText, setCommentText] = useState('');
  const [comments, setComments] = useState<any[]>(initialComments);
  const [submittingComment, setSubmittingComment] = useState(false);
  const [commentMenuOpenId, setCommentMenuOpenId] = useState<number | null>(null);

  useEffect(() => {
    setComments(initialComments);
  }, [initialComments]);

  const getAvatarUrl = (avatarUrl?: string): string => {
    return getFullUrl(avatarUrl) || defaultProfile;
  };

  const handleDeleteClick = () => {
    if (window.confirm(t('performance.teamHub.confirmDeletePost'))) {
      onDelete();
    }
  };

  const handleDeleteCommentClick = (commentId: number) => {
    onDeleteComment(commentId);
    setCommentMenuOpenId(null);
  };

  const handleCommentSubmit = async () => {
    if (!commentText.trim()) return;

    setSubmittingComment(true);
    try {
      await onComment(commentText);
      setCommentText('');
    } catch (error) {
      console.error('Error submitting comment:', error);
    } finally {
      setSubmittingComment(false);
    }
  };

  const handleToggleComments = () => {
    setShowComments(!showComments);
  };

  const handleKeyPress = (event: React.KeyboardEvent) => {
    if (event.key === 'Enter' && !event.shiftKey) {
      event.preventDefault();
      handleCommentSubmit();
    }
  };

  const formatDate = (date: Date) => {
    return format(new Date(date), 'MMM d, yyyy • h:mm a');
  };

  const getUpdateTypeColor = (type: string): string => {
    const colors: { [key: string]: string } = {
      'team-achievement': 'text-green-600',
      'progress-milestone': 'text-blue-600',
      'goal-update': 'text-orange-500',
      'recognition-highlight': 'text-pink-600',
      'update': 'text-purple-600',
      'announcement': 'text-indigo-600',
      'question': 'text-red-500',
      'milestone': 'text-teal-600',
    };
    return colors[type] || 'text-muted-foreground';
  };

  const getUpdateTypeBg = (type: string): string => {
    const bgColors: { [key: string]: string } = {
      'team-achievement': 'bg-green-600/10',
      'progress-milestone': 'bg-blue-600/10',
      'goal-update': 'bg-orange-500/10',
      'recognition-highlight': 'bg-pink-600/10',
      'update': 'bg-purple-600/10',
      'announcement': 'bg-indigo-600/10',
      'question': 'bg-red-500/10',
      'milestone': 'bg-teal-600/10',
    };
    return bgColors[type] || 'bg-muted';
  };

  const getUpdateTypeLabel = (type: string) => {
    const labels: { [key: string]: string } = {
      'team-achievement': t('performance.teamHub.typeTeamAchievement'),
      'progress-milestone': t('performance.teamHub.typeProgressMilestone'),
      'goal-update': t('performance.teamHub.typeGoalUpdate'),
      'recognition-highlight': t('performance.teamHub.typeRecognition'),
      'update': t('performance.teamHub.typeUpdate'),
      'announcement': t('performance.teamHub.typeAnnouncement'),
      'question': t('performance.teamHub.typeQuestion'),
      'milestone': t('performance.teamHub.typeMilestone'),
    };
    return labels[type] || type.charAt(0).toUpperCase() + type.slice(1).replace('-', ' ');
  };

  return (
    <Card className="mb-3 w-full rounded-lg border border-border shadow-none hover:shadow-md transition-shadow">
      <CardHeader className="pb-1">
        <div className="flex items-start justify-between">
          <div className="flex items-start gap-3">
            {/* Avatar */}
            <img
              src={getAvatarUrl(update.author.avatar)}
              alt={update.author.avatarAlt}
              className="w-12 h-12 rounded-full object-cover"
              onError={(e) => {
                (e.target as HTMLImageElement).src = defaultProfile;
              }}
            />
            <div>
              <p className="text-sm font-medium text-foreground">
                {update.author.name}
              </p>
              <div className="flex items-center gap-1 flex-wrap">
                <span className="text-xs text-muted-foreground">
                  {formatDate(update.timestamp)}
                </span>
                {update.department && (
                  <>
                    <span className="text-xs text-muted-foreground">&bull;</span>
                    <span className="inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium border border-border text-foreground">
                      {update.department}
                    </span>
                  </>
                )}
                {update.type && (
                  <>
                    <span className="text-xs text-muted-foreground">&bull;</span>
                    <span
                      className={cn(
                        'inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium',
                        getUpdateTypeBg(update.type),
                        getUpdateTypeColor(update.type)
                      )}
                    >
                      {getUpdateTypeLabel(update.type)}
                    </span>
                  </>
                )}
              </div>
            </div>
          </div>

          {/* Actions Menu */}
          {isAuthor && (
            <DropdownMenu>
              <DropdownMenuTrigger asChild>
                <button className="p-1 rounded-md hover:bg-muted">
                  <AppIcon name="MoreVertical" size={20} />
                </button>
              </DropdownMenuTrigger>
              <DropdownMenuContent>
                <DropdownMenuItem onClick={handleDeleteClick}>
                  <AppIcon name="Trash2" className="me-2" size={16} />
                  {t('performance.teamHub.deletePost')}
                </DropdownMenuItem>
              </DropdownMenuContent>
            </DropdownMenu>
          )}
        </div>
      </CardHeader>

      <CardContent className="pt-1">
        <p className="text-sm text-foreground whitespace-pre-wrap mb-2">
          {update.content}
        </p>

        {update.attachments && update.attachments.length > 0 && (
          <div className="mt-2 mb-2">
            <span className="text-xs text-muted-foreground block mb-1">
              {t('performance.teamHub.attachments')}:
            </span>
            <div className="flex flex-wrap gap-1">
              {update.attachments.map((file) => (
                <Button
                  key={file.id}
                  variant="outline"
                  size="sm"
                >
                  <AppIcon name="Paperclip" size={14} className="me-1" />
                  {file.name}
                </Button>
              ))}
            </div>
          </div>
        )}

        {/* Comments Section */}
        <div className="mt-2">
          <Button
            variant="ghost"
            size="sm"
            onClick={handleToggleComments}
          >
            <AppIcon name="MessageCircle" size={16} className="me-1" />
            {comments.length} {comments.length === 1 ? t('performance.teamHub.comment') : t('performance.teamHub.comments')}
          </Button>

          {showComments && (
            <div className="mt-3">
              <hr className="border-border my-2" />

              {/* Comments List */}
              {comments.length > 0 ? (
                <div className="mb-2">
                  {comments.map((comment) => (
                    <div
                      key={comment.id}
                      className="mb-2 p-2 rounded-lg bg-muted/50"
                    >
                      <div className="flex items-start">
                        <img
                          src={getAvatarUrl(comment.authorAvatar)}
                          alt={comment.authorName}
                          className="w-8 h-8 rounded-full object-cover me-2 flex-shrink-0"
                          onError={(e) => {
                            (e.target as HTMLImageElement).src = defaultProfile;
                          }}
                        />
                        <div className="flex-1 min-w-0">
                          <span className="text-xs font-medium text-foreground">
                            {comment.authorName}
                          </span>
                          <p className="text-sm text-foreground mt-0.5">
                            {comment.content}
                          </p>
                          <span className="text-xs text-muted-foreground mt-0.5 block">
                            {format(new Date(comment.commentedAt), 'MMM d, h:mm a')}
                          </span>
                        </div>
                        {canDeleteComment(comment.authorId) && (
                          <DropdownMenu
                            open={commentMenuOpenId === comment.id}
                            onOpenChange={(open) =>
                              setCommentMenuOpenId(open ? comment.id : null)
                            }
                          >
                            <DropdownMenuTrigger asChild>
                              <button className="p-1 rounded-md hover:bg-muted flex-shrink-0">
                                <AppIcon name="MoreVertical" size={16} />
                              </button>
                            </DropdownMenuTrigger>
                            <DropdownMenuContent>
                              <DropdownMenuItem
                                onClick={() => handleDeleteCommentClick(comment.id)}
                              >
                                <AppIcon name="Trash2" className="me-2" size={14} />
                                {t('performance.teamHub.deleteComment')}
                              </DropdownMenuItem>
                            </DropdownMenuContent>
                          </DropdownMenu>
                        )}
                      </div>
                    </div>
                  ))}
                </div>
              ) : (
                <p className="text-sm text-muted-foreground text-center py-2 mb-2">
                  {t('performance.teamHub.noComments')}
                </p>
              )}

              {/* Add Comment */}
              <div className="flex gap-1 items-start">
                <textarea
                  className="flex-1 rounded-lg border border-border bg-background px-3 py-2 text-sm text-foreground placeholder:text-muted-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary/20 focus-visible:border-primary resize-none"
                  rows={1}
                  placeholder={t('performance.teamHub.addCommentPlaceholder')}
                  value={commentText}
                  onChange={(e) => setCommentText(e.target.value)}
                  onKeyPress={handleKeyPress}
                  disabled={submittingComment}
                />
                <button
                  className="p-2 rounded-md hover:bg-muted disabled:opacity-50 disabled:cursor-not-allowed"
                  onClick={handleCommentSubmit}
                  disabled={!commentText.trim() || submittingComment}
                >
                  {submittingComment ? (
                    <Spinner size="sm" />
                  ) : (
                    <AppIcon name="Send" size={20} />
                  )}
                </button>
              </div>
            </div>
          )}
        </div>
      </CardContent>
    </Card>
  );
};

export default UpdateCard;
